// React query useQuery and useMutation implementations across the DevHub API
//
// Query key hierarchy and naming is based on the pattern described here:
// https://tkdodo.eu/blog/effective-react-query-keys#structure

import axios from 'axios';

export const getCookieValue = (name) =>
  document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || '';

// key in `whitelabel.oneoff_access_group_ids` for read-only template site
// access, an optional group alongside Business/BusinessLocation/Site
export const TEMPLATE_SITE_ACCESS_GROUP = 'TemplateSite';

const detailQuery = (resource_name, id, options = {}, params = {}) => {
  // useQuery generator for single item "detail" pattern for our API
  //
  // example: GET /api/v2/sites/12345/
  // detailQuery('sites', siteID, options)
  const queryOptions = {
    enabled: !!id,
    queryKey: [resource_name, 'detail', id, params],
    queryFn: async () => {
      const query = await axios
        .get(`/api/v2/${resource_name}/${id}/`, { params })
        .then((response) => {
          return response.data;
        });
      return query;
    },
    ...options,
  };
  return queryOptions;
};

const detailMutation = (resource_name, id, options = {}) => {
  // useMutation generator for single item "detail" pattern for our API
  //
  // example: PUT /api/v2/sites/12345/
  // detailMutation('sites', siteID, options)
  const queryOptions = {
    mutationFn: async (payload) => {
      // allow passing of the primary key in the payload if desired
      const pk = payload.id || id;
      if (pk) {
        // update call, uses PUT and the primary key (ID)
        const query = await axios
          .put(`/api/v2/${resource_name}/${pk}/`, payload, {
            headers: {
              'content-type': 'application/json',
              'x-csrftoken': getCookieValue('csrftoken'),
            },
          })
          .then((response) => {
            return response.data;
          });
        return query;
      } else {
        // no id implies a POST/create
        const query = await axios
          .post(`/api/v2/${resource_name}/`, payload, {
            headers: {
              'content-type': 'application/json',
              'x-csrftoken': getCookieValue('csrftoken'),
            },
          })
          .then((response) => {
            return response.data;
          });
        return query;
      }
    },
    ...options,
  };
  return queryOptions;
};

const deleteMutation = (resource_name, id, options = {}) => {
  // useMutation generator for single item delete pattern for our API
  //
  // example: DELETE /api/v2/sites/12345/
  // deleteMutation('sites', siteID, options)
  const queryOptions = {
    mutationFn: async () => {
      // update call, uses DELETE and the primary key (ID)
      const query = await axios
        .delete(`/api/v2/${resource_name}/${id}/`, {
          headers: {
            'content-type': 'application/json',
            'x-csrftoken': getCookieValue('csrftoken'),
          },
        })
        .then((response) => {
          return response;
        });
      return query;
    },
    ...options,
  };
  return queryOptions;
};

const listQuery = (resource_name, params = {}, options = {}) => {
  // useQuery generator for resource "list"
  //
  // example: GET /api/v2/sites/?params=
  // listQuery('sites', params={}, options)
  const queryOptions = {
    queryKey: [resource_name, 'list', params],
    queryFn: async () => {
      const query = await axios
        .get(`/api/v2/${resource_name}/`, {
          params: params,
        })
        .then((response) => {
          return response.data;
        });
      return query;
    },
    ...options,
  };
  return queryOptions;
};

const fetchAllPages = async (resource_name, params = {}, allData = []) => {
  // fetch all pages of a list query
  //
  // example: fetchAllPages('sites', params={})
  if (!params.limit) {
    params.limit = 500;
  }
  const response = await axios.get(`/api/v2/${resource_name}/`, {
    params: params,
  });
  const newData = response.data.objects;
  allData = [...allData, ...newData];

  const hasNext = response.data.meta.next != null;
  const gotData = newData.length > 0;

  if (hasNext && gotData && response.data.meta.total_count > allData.length) {
    // fetch next page until we have all the data
    if (!params.offset) {
      // initial offset is not set, set it to 0
      params.offset = 0;
    }
    params.offset += params.limit;
    return fetchAllPages(resource_name, params, allData);
  } else {
    // we have all the data, return the dataset
    return {
      ...response.data, // include the meta data from last page
      objects: allData,
    };
  }
};

const recursiveListQuery = (resource_name, params = {}, options = {}) => {
  // useQuery generator for resource "list" with recursive pagination
  //
  // example: GET /api/v2/sites/?params=
  // listQuery('sites', params={}, options)
  const queryOptions = {
    queryKey: [resource_name, 'list', params],
    queryFn: async () => fetchAllPages(resource_name, params),
    ...options,
  };
  return queryOptions;
};

const accessGroupQuerystring = (accessGroup) => {
  // request an alternate whitelabel access group on the access endpoints
  //
  // omitted entirely for the default group so existing calls are unchanged
  return accessGroup ? `?access_group=${encodeURIComponent(accessGroup)}` : '';
};

const accessQuery = (resource_name, id, options = {}, accessGroup = null) => {
  // example: GET /api/v2/sites/12345/users/
  // example: GET /api/v2/sites/12345/users/?access_group=TemplateSite
  const queryOptions = {
    enabled: !!id,
    queryKey: [`${resource_name}_access`, 'list', id, accessGroup],
    queryFn: async () => {
      const query = await axios
        .get(
          `/api/v2/${resource_name}/${id}/users/` +
            accessGroupQuerystring(accessGroup),
        )
        .then((response) => {
          return response.data;
        });
      return query;
    },
    ...options,
  };
  return queryOptions;
};

const accessMutation = (
  resource_name,
  id,
  options = {},
  accessGroup = null,
) => {
  // example: PUT /api/v2/sites/12345/users/
  // example: PUT /api/v2/sites/12345/users/?access_group=TemplateSite
  const queryOptions = {
    mutationFn: async (payload) => {
      const query = await axios
        .put(
          `/api/v2/${resource_name}/${id}/users/` +
            accessGroupQuerystring(accessGroup),
          payload,
          {
            headers: {
              'content-type': 'application/json',
              'x-csrftoken': getCookieValue('csrftoken'),
            },
          },
        )
        .then((response) => {
          return response.data;
        });
      return query;
    },
    ...options,
  };
  return queryOptions;
};

export const authDetailsQuery = (options = {}) => {
  // useQuery for auth details (user, whitelabel, etc)
  // GET /resources/auth_details/
  const queryOptions = {
    queryKey: ['auth_details'],
    queryFn: async () => {
      const query = await axios
        .get(`/resources/auth_details/`)
        .then((response) => {
          return response.data;
        });
      return query;
    },
    ...options,
  };
  return queryOptions;
};

export const authorDeleteMutation = (authorID, options = {}) => {
  // useMutation for single author delete
  // DELETE /api/v2/authors/:id/
  return deleteMutation('authors', authorID, options);
};

export const authorsQuery = (params = {}, options = {}) => {
  // useQuery for author list
  // /api/v2/authors/
  return listQuery('authors', params, options);
};

export const authorQuery = (authorID, options = {}) => {
  // useQuery for single author
  // GET /api/v2/authors/:id/
  return detailQuery('authors', authorID, options);
};

export const authorMutation = (authorID, options = {}) => {
  // useMutation for single author
  // PUT /api/v2/authors/:id/
  return detailMutation('authors', authorID, options);
};

export const blogCategoryDeleteMutation = (categoryID, options = {}) => {
  // useMutation for single category delete
  // DELETE /api/v2/categories/:id/
  return deleteMutation('categories', categoryID, options);
};

export const blogCategoriesQuery = (params = {}, options = {}) => {
  // useQuery for groups list
  // /api/v2/categories/
  return listQuery('categories', params, options);
};

export const blogCategoryQuery = (categoryID, options = {}) => {
  // useQuery for single category
  // GET /api/v2/categories/:id/
  return detailQuery('categories', categoryID, options);
};

export const blogCategoryMutation = (categoryID, options = {}) => {
  // useMutation for single category
  // PUT /api/v2/categories/:id/
  return detailMutation('categories', categoryID, options);
};

export const businessQuery = (businessID, options = {}) => {
  // useQuery for single business
  // GET /api/v2/businesses/:id/
  return detailQuery('businesses', businessID, options);
};

export const businessMutation = (businessID, options = {}) => {
  // useMutation for single business
  // PUT /api/v2/businesses/:id/
  return detailMutation('businesses', businessID, options);
};

export const businessesQuery = (params, options = {}) => {
  // useQuery for businesses list
  // /api/v2/businesses/
  return listQuery('businesses', params, options);
};

export const businessDeleteMutation = (businessID, options = {}) => {
  // useMutation for single business delete
  // DELETE /api/v2/businesses/:id/
  return deleteMutation('businesses', businessID, options);
};

export const businessAccessQuery = (businessID, options = {}) => {
  // example: GET /api/v2/businesses/12345/users/
  return accessQuery('businesses', businessID, options);
};

export const businessAccessMutation = (businessID, options = {}) => {
  // example: PUT /api/v2/businesses/12345/users/
  return accessMutation('businesses', businessID, options);
};

export const constantsQuery = (options = {}) => {
  // useQuery for constants (datasets for select fields, etc)
  // GET /resources/constants/
  const queryOptions = {
    queryKey: ['static_globals'],
    queryFn: async () => {
      const query = await axios
        .get(`/resources/constants/`)
        .then((response) => {
          return response.data;
        });
      return query;
    },
    // set default staleTime of Infinity to never have the client reload
    staleTime: Infinity,
    ...options,
  };
  return queryOptions;
};

export const clearCacheMutation = (options = {}) => {
  // useMutation for clearing cache
  // POST /resources/clear_cache/
  const queryOptions = {
    mutationFn: async () => {
      const query = await axios
        .post(
          `/resources/clear_cache/`,
          {},
          {
            headers: {
              'content-type': 'application/json',
              'x-csrftoken': getCookieValue('csrftoken'),
            },
          },
        )
        .then((response) => {
          return response.data;
        });
      return query;
    },
    ...options,
  };
  return queryOptions;
};

export const datamodelQuery = (datamodelID, options = {}) => {
  // useQuery for single datamodel
  // GET /api/v2/datamodels/:id/
  return detailQuery('datamodels', datamodelID, options);
};

export const datamodelMutation = (datamodelID, options = {}) => {
  // useMutation for single datamodel
  // PUT /api/v2/datamodels/:id/
  return detailMutation('datamodels', datamodelID, options);
};

export const datamodelDeleteMutation = (datamodelID, options = {}) => {
  // useMutation for single datamodel delete
  // DELETE /api/v2/datamodels/:id/
  return deleteMutation('datamodels', datamodelID, options);
};

export const datamodelsQuery = (params = {}, options = {}) => {
  // useQuery for datamodels list
  // /api/v2/datamodels/
  return listQuery('datamodels', params, options);
};

export const datamodelItemQuery = (datamodelItemID, options = {}) => {
  // useQuery for single datamodel item
  // GET /api/datamodels/items/:id/
  return detailQuery('datamodels/items', datamodelItemID, options);
};

export const datamodelItemMutation = (datamodelItemID, options = {}) => {
  // useMutation for single datamodel item
  // PUT /api/datamodels/items/:id/
  return detailMutation('datamodels/items', datamodelItemID, options);
};

export const datamodelItemDeleteMutation = (datamodelItemID, options = {}) => {
  // useMutation for single datamodel item delete
  // DELETE /api/datamodels/items/:id/
  return deleteMutation('datamodels/items', datamodelItemID, options);
};

export const datamodelItemsQuery = (params = {}, options = {}) => {
  // useQuery for datamodel items list
  // /api/v2/datamodels/items/
  return listQuery('datamodels/items', params, options);
};

export const datamodelSubTypeQuery = (params = {}, options = {}) => {
  // useQuery for datamodel sub types
  // /api/v2/datamodels/items/
  return listQuery('datamodels/items', params, options);
};

export const groupsQuery = (params = {}, options = {}) => {
  // useQuery for groups list
  // /api/v2/groups/
  return listQuery('groups', params, options);
};

export const groupQuery = (groupID, options = {}) => {
  // useQuery for single group
  // GET /api/v2/groups/:id/
  return detailQuery('groups', groupID, options);
};

export const groupMutation = (groupID, options = {}) => {
  // useMutation for single group
  // PUT /api/v2/groups/:id/
  return detailMutation('groups', groupID, options);
};

export const imageQuery = (imageID, options = {}) => {
  // useQuery for single image
  // GET /api/v2/images/:id/
  // do not reload on window refocus
  options.refetchOnWindowFocus = false;
  options.staleTime = Infinity;
  return detailQuery('images', imageID, options);
};

export const imageMutation = (imageID, options = {}) => {
  // useMutation for single image
  // PUT /api/v2/images/:id/
  return detailMutation('images', imageID, options);
};

export const linklistQuery = (linklistID, options = {}) => {
  // useQuery for single image
  // GET /api/v2/linklists/:id/
  return detailQuery('linklists', linklistID, options);
};

export const linklistsQuery = (params = {}, options = {}) => {
  // useQuery for groups list
  // /api/v2/linklists/
  return listQuery('linklists', params, options);
};

export const linklistMutation = (linklistID, options = {}) => {
  // useMutation for single linklist
  // PUT /api/v2/linklists/:id/
  return detailMutation('linklists', linklistID, options);
};

export const locationQuery = (locationID, options = {}) => {
  // useQuery for single location
  // GET /api/v2/locations/:id/
  return detailQuery('locations', locationID, options);
};

export const locationMutation = (locationID, options = {}) => {
  // useMutation for single location
  // PUT /api/v2/locations/:id/
  return detailMutation('locations', locationID, options);
};

export const locationsQuery = (params = {}, options = {}) => {
  // useQuery for locations list
  // /api/v2/locations/
  return listQuery('locations', params, options);
};

export const locationDeleteMutation = (locationID, options = {}) => {
  // useMutation for single location delete
  // DELETE /api/v2/locations/:id/
  return deleteMutation('locations', locationID, options);
};

export const locationAccessQuery = (locationID, options = {}) => {
  // example: GET /api/v2/locations/12345/users/
  return accessQuery('locations', locationID, options);
};

export const locationAccessMutation = (locationID, options = {}) => {
  // example: PUT /api/v2/locations/12345/users/
  return accessMutation('locations', locationID, options);
};

export const pageQuery = (pageID, options = {}) => {
  // useQuery for single page
  // GET /api/v2/pages/:id/
  return detailQuery('pages', pageID, options);
};

export const pageMutation = (pageID, options = {}) => {
  // useMutation for single page
  // PUT /api/v2/pages/
  return detailMutation('pages', pageID, options);
};

export const pagesQuery = (params = {}, options = {}) => {
  // useQuery for list of site pages
  // /api/v2/pages/
  return recursiveListQuery('pages', params, options);
};

export const pageDeleteMutation = (pageID, options = {}) => {
  // useMutation for single page delete
  // DELETE /api/v2/pages/:id/
  return deleteMutation('pages', pageID, options);
};

export const postDeleteMutation = (postID, options = {}) => {
  // useMutation for single post delete
  // DELETE /api/v2/posts/:id/
  return deleteMutation('posts', postID, options);
};

export const postsQuery = (params = {}, options = {}) => {
  // useQuery for groups list
  // /api/v2/posts/
  return listQuery('posts', params, options);
};

export const postQuery = (postID, options = {}) => {
  // useQuery for single post
  // GET /api/v2/posts/:id/
  return detailQuery('posts', postID, options);
};

export const postMutation = (postID, options = {}) => {
  // useMutation for single post
  // PUT /api/v2/posts/:id/
  return detailMutation('posts', postID, options);
};

export const siteAccessQuery = (siteID, options = {}) => {
  // example: GET /api/v2/sites/12345/users/
  return accessQuery('sites', siteID, options);
};

export const siteAccessMutation = (siteID, options = {}) => {
  // example: PUT /api/v2/sites/12345/users/
  return accessMutation('sites', siteID, options);
};

export const templateSiteAccessQuery = (siteID, options = {}) => {
  // read-only access to a site used as a template
  // example: GET /api/v2/sites/12345/users/?access_group=TemplateSite
  return accessQuery('sites', siteID, options, TEMPLATE_SITE_ACCESS_GROUP);
};

export const templateSiteAccessMutation = (siteID, options = {}) => {
  // read-only access to a site used as a template
  // example: PUT /api/v2/sites/12345/users/?access_group=TemplateSite
  return accessMutation('sites', siteID, options, TEMPLATE_SITE_ACCESS_GROUP);
};

export const sitesQuery = (params = {}, options = {}) => {
  // useQuery for groups list
  // /api/v2/sites/
  return listQuery('sites', params, options);
};

export const siteQuery = (siteID, options = {}, params = {}) => {
  // useQuery for single site
  // GET /api/v2/sites/:id/
  return detailQuery('sites', siteID, options, params);
};

export const siteMutation = (siteID, options = {}) => {
  // useMutation for single site
  // PUT /api/v2/sites/:id/
  return detailMutation('sites', siteID, options);
};

export const siteDeleteMutation = (siteID, options = {}) => {
  // useMutation for single site delete
  // DELETE /api/v2/sites/:id/
  return deleteMutation('sites', siteID, options);
};

export const draftDeleteMutation = (draftID, options = {}) => {
  // useMutation for single draft delete, used to discard a pending draft
  // DELETE /api/v2/drafts/:id/
  return deleteMutation('drafts', draftID, options);
};

export const themeQuery = (themeID, options = {}) => {
  // useQuery for single theme
  // GET /api/v2/themes/:id/
  return detailQuery('themes', themeID, options);
};

export const themeMutation = (themeID, options = {}) => {
  // useMutation for single theme
  // PUT /api/v2/themes/:id/
  return detailMutation('themes', themeID, options);
};

export const userQuery = (userID, options = {}) => {
  // useQuery for single user
  // GET /api/v2/users/:id/
  return detailQuery('users', userID, options);
};

export const userMutation = (userID, options = {}) => {
  // useMutation for single user
  // PUT /api/v2/users/:id/
  return detailMutation('users', userID, options);
};

export const userDeleteMutation = (userID, options = {}) => {
  // useMutation for single user delete
  // DELETE /api/v2/users/:id/
  return deleteMutation('users', userID, options);
};

export const usersQuery = (params = {}, options = {}) => {
  // useQuery for users list
  // /api/v2/users/
  return listQuery('users', params, options);
};
